import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Bell, Settings, HelpCircle, Sun, Moon, Monitor, Check, X, ExternalLink, Search, GraduationCap, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTutorial, TutorialModuleList } from './TutorialSystem';
import { playNotificationSound, areSoundsEnabled, setSoundsEnabled } from '../utils/notificationSound';

const API = process.env.REACT_APP_BACKEND_URL;

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/preferences/notifications?limit=20`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 15 seconds (faster for portal submissions)
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/api/preferences/notifications/${id}/read`, {}, { headers });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/preferences/notifications/mark-all-read`, {}, { headers });
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/api/preferences/notifications/${id}`, { headers });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (type) => {
    const icons = {
      task: '📋',
      deal: '💰',
      announcement: '📢',
      system: '⚙️',
      support: '🎧',
      security_alert: '🔒',
      portal_ticket: '🎫',
      portal_booking: '📅',
      bug_report: '🐛',
      customer_submission: '📬',
      live_chat_request: '💬'
    };
    return icons[type] || '📌';
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No notifications
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-3 hover:bg-slate-50 cursor-pointer border-b last:border-0 ${!notif.is_read ? 'bg-blue-50' : ''}`}
                onClick={() => !notif.is_read && markAsRead(notif.id)}
              >
                <div className="flex items-start gap-2">
                  <span className="text-lg">{getIcon(notif.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.is_read ? 'font-medium' : ''}`}>{notif.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{notif.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatTime(notif.created_at)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Customer Portal Notifications (for admins - shows pending submissions)
export const CustomerPortalNotifications = () => {
  const [pendingData, setPendingData] = useState({
    pending_files: 0,
    pending_testimonials: 0,
    open_tickets: 0,
    pending_bookings: 0,
    total_pending: 0,
    recent_files: [],
    recent_testimonials: [],
    recent_tickets: [],
    recent_bookings: []
  });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(areSoundsEnabled());
  const previousCount = useRef(0);

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchPendingCounts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/customer-portal/admin/pending-counts`, { headers });
      const newData = res.data;
      
      // Play notification sound if count increased
      if (newData.total_pending > previousCount.current && previousCount.current > 0) {
        playNotificationSound('notification', 0.3);
        toast.info('New customer submission received!', {
          description: `${newData.total_pending} pending items`
        });
      }
      
      previousCount.current = newData.total_pending;
      setPendingData(newData);
    } catch (err) {
      // Silently fail for non-admins
      console.debug('Customer portal notifications not available:', err.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingCounts();
    // Poll every 15 seconds for portal submissions
    const interval = setInterval(fetchPendingCounts, 15000);
    return () => clearInterval(interval);
  }, [fetchPendingCounts]);

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    setSoundsEnabled(newState);
    if (newState) {
      playNotificationSound('success', 0.2);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getCategoryColor = (category) => {
    const colors = {
      issue: 'bg-red-100 text-red-700',
      success: 'bg-emerald-100 text-emerald-700',
      maintenance: 'bg-blue-100 text-blue-700',
      general: 'bg-slate-100 text-slate-700'
    };
    return colors[category] || colors.general;
  };

  // Don't render if no pending items and not loading
  if (pendingData.total_pending === 0 && !loading) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative" 
          data-testid="customer-portal-notifications"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {pendingData.total_pending > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-purple-500 text-white text-xs flex items-center justify-center animate-pulse">
              {pendingData.total_pending > 9 ? '9+' : pendingData.total_pending}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Customer Submissions</span>
          <Badge variant="outline" className="bg-purple-50 text-purple-700">
            {pendingData.total_pending} pending
          </Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Pending Files Section */}
        {pendingData.pending_files > 0 && (
          <>
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-2">
              <span>📁</span> Files ({pendingData.pending_files})
            </div>
            {pendingData.recent_files.map((file) => (
              <DropdownMenuItem 
                key={file.id} 
                className="cursor-pointer"
                onClick={() => window.location.href = '/customer-portal-admin'}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.title}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{file.contact_name}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${getCategoryColor(file.category)}`}>
                      {file.category}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{formatTime(file.created_at)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Pending Testimonials Section */}
        {pendingData.pending_testimonials > 0 && (
          <>
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-2">
              <span>💬</span> Testimonials ({pendingData.pending_testimonials})
            </div>
            {pendingData.recent_testimonials.map((testimonial) => (
              <DropdownMenuItem 
                key={testimonial.id} 
                className="cursor-pointer"
                onClick={() => window.location.href = '/customer-portal-admin'}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{testimonial.title}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{testimonial.contact_name}</span>
                    <span className="text-yellow-500">{'⭐'.repeat(testimonial.rating)}</span>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{formatTime(testimonial.created_at)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Support Tickets Section */}
        {pendingData.open_tickets > 0 && (
          <>
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-2">
              <span>🎫</span> Support Tickets ({pendingData.open_tickets})
            </div>
            {pendingData.recent_tickets?.map((ticket) => (
              <DropdownMenuItem 
                key={ticket.id} 
                className="cursor-pointer"
                onClick={() => window.location.href = `/tickets?id=${ticket.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{ticket.subject || ticket.ticket_number}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{ticket.contact_name || 'Portal Customer'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      ticket.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {ticket.priority}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{formatTime(ticket.created_at)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Pending Bookings Section */}
        {pendingData.pending_bookings > 0 && (
          <>
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-2">
              <span>📅</span> Meeting Requests ({pendingData.pending_bookings})
            </div>
            {pendingData.recent_bookings?.map((booking) => (
              <DropdownMenuItem 
                key={booking.id} 
                className="cursor-pointer"
                onClick={() => window.location.href = `/calendar`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{booking.customer_name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{booking.customer_email}</span>
                    <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                      {new Date(booking.scheduled_datetime).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{formatTime(booking.created_at)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Sound Toggle */}
        <div className="px-2 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            {soundEnabled ? <Volume2 className="h-4 w-4 text-slate-500" /> : <VolumeX className="h-4 w-4 text-slate-400" />}
            <span className="text-slate-600">Sound alerts</span>
          </div>
          <Switch 
            checked={soundEnabled} 
            onCheckedChange={toggleSound}
            data-testid="portal-sound-toggle"
          />
        </div>
        <DropdownMenuSeparator />
        
        {/* View All Link */}
        <DropdownMenuItem 
          className="cursor-pointer text-center justify-center text-purple-600 font-medium"
          onClick={() => window.location.href = '/customer-portal-admin'}
        >
          View All in Customer Portal
          <ExternalLink className="h-3 w-3 ml-1" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ThemeToggle = ({ variant = 'icon' }) => {
  const [theme, setTheme] = useState('light');

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    // Load theme from preferences or localStorage
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, []);

  const applyTheme = (newTheme) => {
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else if (newTheme === 'light') {
      root.classList.remove('dark');
    } else {
      // System preference
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    }
  };

  const changeTheme = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    applyTheme(newTheme);
    
    // Save to backend
    try {
      await axios.put(`${API}/api/preferences/me/theme?theme=${newTheme}`, {}, { headers });
    } catch (err) {
      console.error('Failed to save theme preference:', err);
    }
  };

  if (variant === 'dropdown') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" data-testid="theme-toggle">
            {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => changeTheme('light')} className="cursor-pointer">
            <Sun className="h-4 w-4 mr-2" />
            Light
            {theme === 'light' && <Check className="h-4 w-4 ml-auto" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => changeTheme('dark')} className="cursor-pointer">
            <Moon className="h-4 w-4 mr-2" />
            Dark
            {theme === 'dark' && <Check className="h-4 w-4 ml-auto" />}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => changeTheme('system')} className="cursor-pointer">
            <Monitor className="h-4 w-4 mr-2" />
            System
            {theme === 'system' && <Check className="h-4 w-4 ml-auto" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => changeTheme(theme === 'dark' ? 'light' : 'dark')}
      data-testid="theme-toggle-simple"
    >
      {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
};

export const HelpButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('help'); // 'help' or 'tutorials'

  const getToken = () => localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchResources = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await axios.get(`${API}/api/preferences/help?${params}`, { headers });
      setResources(res.data);
    } catch (err) {
      console.error('Failed to fetch help resources:', err);
    }
  }, [selectedCategory, searchQuery]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/preferences/help/categories`, { headers });
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchResources();
    }
  }, [isOpen, fetchCategories, fetchResources]);

  useEffect(() => {
    if (isOpen) {
      fetchResources();
    }
  }, [selectedCategory, searchQuery]);

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)} data-testid="help-button">
        <HelpCircle className="h-5 w-5" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Help & Resources
            </DialogTitle>
            <DialogDescription>
              Find guides, tutorials, and support resources
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex gap-2 border-b pb-2">
            <Button
              variant={activeTab === 'help' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('help')}
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Help Articles
            </Button>
            <Button
              variant={activeTab === 'tutorials' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('tutorials')}
            >
              <GraduationCap className="h-4 w-4 mr-2" />
              Interactive Tutorials
            </Button>
          </div>

          {activeTab === 'help' ? (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={selectedCategory === null ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(null)}
                >
                  All
                </Badge>
                {categories.map((cat) => (
                  <Badge
                    key={cat.id}
                    variant={selectedCategory === cat.id ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.icon} {cat.name}
                  </Badge>
                ))}
              </div>

              {/* Resources */}
              <ScrollArea className="h-[350px]">
                <div className="space-y-3">
                  {resources.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No help resources found
                    </div>
                  ) : (
                    resources.map((resource) => (
                      <div key={resource.id} className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {resource.resource_type}
                              </Badge>
                              <h4 className="font-medium">{resource.title}</h4>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                            {resource.tags?.length > 0 && (
                              <div className="flex gap-1 mt-2">
                                {resource.tags.map(tag => (
                                  <span key={tag} className="text-xs text-muted-foreground">#{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          {resource.url && (
                            <a href={resource.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 text-muted-foreground" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Take interactive walkthroughs to learn how to use different parts of UnifiCRM.
              </p>
              <ScrollArea className="h-[400px]">
                <TutorialModuleList />
              </ScrollArea>
            </div>
          )}

          {/* Contact Support */}
          <div className="text-center text-sm text-muted-foreground border-t pt-4">
            Need more help? Contact <a href="mailto:support@unificrm.com" className="text-blue-500 hover:underline">support@unificrm.com</a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const SettingsMenu = () => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="settings-menu">
          <Settings className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Quick Settings</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-2">
          <p className="text-sm font-medium mb-2">Theme</p>
          <div className="flex gap-2">
            <ThemeToggle variant="dropdown" />
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href="/settings" className="cursor-pointer">
            All Settings
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default { NotificationBell, ThemeToggle, HelpButton, SettingsMenu };
